import { c as _c } from "react/compiler-runtime";
import classNames from 'classnames';
import { type RefObject, memo, useCallback, useEffect, useRef } from 'react';
import styles from './server/elements/Table.module.css';
import { useResizeContext } from './utils/TableStore';
function calcTotalOffset(ele: HTMLElement, initialOffset = 0): number {
  let parent = ele;
  let offset = initialOffset;
  while (parent !== null) {
    offset += parent.offsetLeft;
    parent = parent.offsetParent as HTMLElement;
  }
  return offset;
}
function isTouchEvent(evt: MouseEvent | TouchEvent): evt is TouchEvent {
  return 'targetTouches' in evt;
}
function useResize<T extends HTMLElement>(ref: RefObject<T | null>, index: number) {
  const {
    onResize,
    setResizing,
    touchEnabled
  } = useResizeContext();
  const onResizeRef = useRef(onResize);
  onResizeRef.current = onResize;
  const animRef = useRef<number>(null);
  const onMouseMove = useCallback((evt: MouseEvent | TouchEvent) => {
    const ele = ref.current;
    if (animRef.current) {
      globalThis.cancelAnimationFrame(animRef.current);
    }
    animRef.current = window.requestAnimationFrame(() => {
      if (!ele) return;
      const offset = calcTotalOffset(ele);
      let clientX = offset;
      if (isTouchEvent(evt)) {
        clientX = evt.targetTouches[0]?.clientX ?? offset;
      } else {
        clientX = evt.clientX;
      }
      const delta = Math.round(clientX) - offset;
      onResizeRef.current(delta, index);
      animRef.current = null;
    });
  }, [ref, animRef, onResizeRef, index]);
  const removeMoveListeners = useCallback(() => {
    const ele_0 = ref.current;
    if (!ele_0) return;
    setResizing(false);
    if (touchEnabled) {
      window.removeEventListener('touchmove', onMouseMove);
      // TODO: Remove this once we have a proper way to handle this
      // eslint-disable-next-line react-hooks/immutability
      window.removeEventListener('touchend', removeMoveListeners);
      return;
    }
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', removeMoveListeners);
  }, [onMouseMove, ref, touchEnabled, setResizing]);
  const addMoveListeners = useCallback(() => {
    const ele_1 = ref.current;
    if (!ele_1) return;
    removeMoveListeners();
    setResizing(true);
    if (touchEnabled) {
      window.addEventListener('touchmove', onMouseMove);
      window.addEventListener('touchend', removeMoveListeners);
      return;
    }
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', removeMoveListeners);
  }, [onMouseMove, ref, removeMoveListeners, touchEnabled, setResizing]);
  const addListeners = useCallback((ele_2: HTMLElement | null) => {
    if (!ele_2) return;
    if (touchEnabled) {
      ele_2.addEventListener('touchstart', addMoveListeners);
      return;
    }
    ele_2.addEventListener('mousedown', addMoveListeners);
  }, [addMoveListeners, touchEnabled]);
  const removeListeners = useCallback((ele_3: HTMLElement | null) => {
    if (!ele_3) return;
    if (typeof animRef.current === 'number') {
      globalThis.cancelAnimationFrame(animRef.current);
    }
    removeMoveListeners();
    if (touchEnabled) {
      ele_3.removeEventListener('touchstart', addMoveListeners);
      return;
    }
    ele_3.removeEventListener('mousedown', addMoveListeners);
  }, [removeMoveListeners, addMoveListeners, animRef, touchEnabled]);
  useEffect(() => {
    const ele_4 = ref.current;
    addListeners(ele_4);
    return () => removeListeners(ele_4);
  }, [ref, addListeners, removeListeners]);
}
function Resize(t0) {
  const $ = _c(4);
  const {
    className,
    index
  } = t0;
  const ref = useRef(null);
  useResize(ref, index);
  let t1;
  if ($[0] !== className) {
    t1 = classNames(styles.resizeHandle, className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className={t1} ref={ref} />;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
export default memo(Resize);